/**
 * The header's login / logout control.
 *
 * Signed out, this is still the always-visible way into an existing account,
 * but it is deliberately no longer the loudest thing in the header. It used to
 * be a solid navy button, which aimed the single strongest call to action on
 * every page at the smallest possible audience: people who already have an
 * account. SiteHeader now owns the primary button ("Find my tools"), and this
 * sits beside it as a text link.
 *
 * It links to /login rather than opening the auth dialog. See the note on the
 * signed-out branch below.
 *
 * Signed in, it shows who you are and offers the way out. All three states
 * render at the same height so the header does not reflow when auth resolves.
 */

import { Link } from "wouter";
import { useAuth } from "@/core/hooks/useAuth";
import { getLoginUrl } from "@/const";

/**
 * Matches the primary button's vertical rhythm without its weight.
 *
 * min-h-11 rather than padding alone: py-2.5 on a 14px line gave a 40px
 * target, which is under the 44px floor every other control on the site now
 * meets. It is deliberately still a link and not a button, so it keeps
 * font-medium and no border.
 */
const LINK_CLASS =
  "inline-flex items-center min-h-11 text-sm font-medium px-1 transition-colors whitespace-nowrap";

const BUTTON_CLASS =
  "text-sm font-bold px-3.5 sm:px-5 py-2.5 rounded-md transition-colors whitespace-nowrap";

export default function AuthControl() {
  const { user, isAuthenticated, loading, logout } = useAuth();

  // Render the placeholder rather than nothing, so the header keeps its height
  // and the Log in link does not appear to jump in a moment after paint.
  if (loading) {
    return (
      <span className={`${LINK_CLASS} opacity-0 select-none`} aria-hidden="true">
        Log in
      </span>
    );
  }

  if (!isAuthenticated) {
    /* Navigates to the dedicated page rather than opening the dialog over the
       current one. The dialog is still what the rest of the app uses when it
       needs an account for a specific thing (the quiz result, a gated guide),
       where staying on the page is the whole point. This control is different:
       someone clicking "Log in" in the header has decided to sign in, not to
       keep reading, so they get a screen for it. */
    return (
      <Link
        href={getLoginUrl()}
        className={`${LINK_CLASS} text-foreground/70 hover:text-foreground`}
      >
        Log in
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {user?.name && (
        <span className="hidden sm:inline text-sm text-muted-foreground">
          Hi, {user.name.split(" ")[0]}
        </span>
      )}
      <button
        onClick={logout}
        className={`${BUTTON_CLASS} bg-secondary text-secondary-foreground border border-border hover:bg-accent hover:text-accent-foreground`}
      >
        Log out
      </button>
    </div>
  );
}
